import { listDispositionsForWorkflow } from '@100mslive/disposition-engine';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Program } from '@compass/client/types/program';
import type { Team } from '@compass/client/types/team';
import { useMemo } from 'react';

import { usePayors } from '@/hooks/usePayors';
import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';

import { filterCasesByPrograms, filterCasesByTeamWorkflowScope } from '../data/filters';
import type { CaseItem } from '../data/types';
import type { TranslateContext } from './internal/buildCaseItem';
import { buildCaseItem } from './internal/buildCaseItem';
import type { ProgramCasesTuple } from './useProgramCases';
import { useProgramCases } from './useProgramCases';
import type { OrgProgramPair } from './useWorkflowSlaMap';
import { useWorkflowSlaMap } from './useWorkflowSlaMap';

/**
 * Data-flow overview for the Workforce Console.
 *
 * Three TanStack-Query data sources feed this hook:
 *
 *   1. `useProgramCases({ tuples })` — fans out one query per (org, program)
 *      (more when a program has over 100 assignees), filtered by
 *      `assigned_user_ids`, and returns the deduped raw case-record list.
 *      `filterCasesByTeamWorkflowScope` then trims that list to the selected
 *      teams' (team → program → workflow-type) reach, so a member
 *      shared across teams doesn't leak a foreign-workflow case into the grid.
 *   2. `useWorkflowSlaMap(slaPairs)` — one query per (org, program); resolves
 *      `workflow_id → sla.minutes` and `workflow_id → Workflow` (the latter
 *      is used to look up `ui.status[state].name` for the display label).
 *   3. `usePayors(enabled)` — paginated payor catalogue (PBM and MedBV share
 *      it); gated on `tuples.length > 0` so an empty console doesn't trigger
 *      the full payor pagination.
 *
 * `buildCaseItem` (in `./internal/buildCaseItem.ts`) is the pure-function
 * translator from `GetCaseRecordResponse` to the `CaseItem` view-model.
 * Per-field resolution rules:
 *
 *   - **Payer**: `extractPayerSlug` forks on workflow type — PBM uses
 *     `payor_name`; MedBV-class uses `primary_payer_name` then falls back
 *     to `payor_name`. The slug is then looked up in `payorNameBySlug`;
 *     unresolved slugs fall back to `EXPRESS SCRIPTS`-style formatted text.
 *   - **Assignee remap**: cases assigned to the logged-in user are routed
 *     into the synthetic "Assigned to me" row by swapping their assignee
 *     id to `ASSIGNED_TO_ME_ID`. Their real user_id only appears via
 *     `currentUserAgent` in the reassign dropdowns.
 *   - **SLA**: delegated to `@/utils/sla.calculateSLA` (same function the
 *     Case List uses via `SLATimer`). The 3-tier risk level
 *     (`healthy`/`warning`/`breached`) is derived from the SLA result.
 *   - **Empty fields**: optional id fields (`activeCallId`, `programId`,
 *     `organizationId`) coerce empty/null to `undefined` via
 *     `emptyToUndefined`; `organizationId` falls back to the page-level
 *     `selectedOrganizationId` when the case record itself doesn't carry it.
 *
 * The column map is total (`findColumnForCase` always resolves a bucket, with
 * unrecognised states landing in `other`), so every fetched case is rendered
 * in exactly one cell and the rollups below describe what's on screen.
 *
 * Three downstream rollups on top of `cases`:
 *
 *   - `kpi` — risk-unfiltered totals (open / at-risk / breached) so the KPI
 *     strip numbers stay stable regardless of which tile is active.
 *   - `filteredCases` — the grid's view, filtered by `riskFilter`. The
 *     KPI tile flip therefore only re-runs the filter, never the full
 *     `buildCaseItem` translation.
 *   - `rawCasesById` — O(1) lookup used by `useWorkforceReassign` to find
 *     a case's `(org, team, program)` from just its id.
 */

// Stable per-module singletons. We intentionally do NOT `Object.freeze`
// these — in strict mode that would throw if a consumer ever mutated the
// hook's return value (an unlikely but high-cost footgun). Identity
// stability across renders is the only thing we actually need here.
const EMPTY_CASES: CaseItem[] = [];
const EMPTY_RAW_BY_ID: Map<string, GetCaseRecordResponse> = new Map();

interface UseWorkforceCasesParams {
  tuples: ProgramCasesTuple[];
  slaPairs: OrgProgramPair[];
  programNameById: Map<string, string>;
  currentUserId: string | null;
  selectedOrganizationId: string | null | undefined;
  riskFilter: 'all' | 'warning' | 'breached';
  selectedProgramIds: string[];
  /** The teams selected in the team switcher — cases are kept only inside their
   *  (team → program → workflow-type) reach (BI-1468 follow-up), so the grid
   *  reflects exactly the selected teams. */
  scopeTeams: Team[];
  /** Eligible programs (with `teams[]`) — resolves which teams serve a case's program. */
  programs: Program[];
}

export function useWorkforceCases({
  tuples,
  slaPairs,
  programNameById,
  currentUserId,
  selectedOrganizationId,
  riskFilter,
  selectedProgramIds,
  scopeTeams,
  programs,
}: UseWorkforceCasesParams) {
  const {
    cases: fetchedCases,
    isLoading: casesLoading,
    isFetching: casesFetching,
    isError: isCasesError,
    isOverFanout,
  } = useProgramCases({ tuples });
  const { slaMap, workflowsById } = useWorkflowSlaMap(slaPairs);

  // Assignee-fetch scopes by (program, assignee-union) but drops the team's
  // workflow-type reach; re-apply it here, against the selected teams, so a
  // selected medbv-only team never shows a shared member's PBM case. Runs on
  // the RAW records (before `buildCaseItem` remaps the current user's assignee
  // to `ASSIGNED_TO_ME_ID`), so the assignee comparison uses the real
  // `assigned_user_id`.
  const rawCases = useMemo(
    () => filterCasesByTeamWorkflowScope(fetchedCases, scopeTeams, programs),
    [fetchedCases, scopeTeams, programs],
  );

  // `usePayors` would paginate the entire payor catalogue on an empty
  // workforce view; gate on "have we got any cases-fetch tuples yet". PBM
  // and MedBV both depend on the result, so the gate doesn't fork on
  // workflow type.
  const { payors } = usePayors(tuples.length > 0);
  const payorNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of payors) {
      if (p.slug && p.name) map.set(p.slug, p.name);
    }
    return map;
  }, [payors]);

  // Resolve one disposition catalog per unique workflow_type in the case set.
  // `listDispositionsForWorkflow` is synchronous (`@100mslive/disposition-engine`
  // ships the catalog in-package) so this is cheap. Workflow types without a
  // catalog throw — caught and skipped so legacy `medbv` cases degrade to "no
  // disposition shown" rather than crashing the grid.
  const dispositionLabelsByWorkflowType = useMemo(() => {
    const byType = new Map<string, Map<number, string>>();
    const seen = new Set<string>();
    for (const c of rawCases) {
      const wt = c.workflow_type;
      if (!wt || seen.has(wt)) continue;
      seen.add(wt);
      try {
        const catalog = listDispositionsForWorkflow(resolveEngineWorkflowType(wt));
        const inner = new Map<number, string>();
        for (const [code, label] of Object.entries(catalog)) {
          inner.set(Number(code), label);
        }
        byType.set(wt, inner);
      } catch {
        // workflow_type without a registered catalog — leave the entry
        // unset so `buildCaseItem` falls through to `dispositionLabel:
        // undefined` and the UI omits the line.
      }
    }
    return byType;
  }, [rawCases]);

  const allCases: CaseItem[] = useMemo(() => {
    if (rawCases.length === 0) return EMPTY_CASES;
    const ctx: TranslateContext = {
      programNameById,
      workflowsById,
      slaMap,
      payorNameBySlug,
      currentUserId,
      selectedOrganizationId,
      dispositionLabelsByWorkflowType,
    };
    return rawCases.map(c => buildCaseItem(c, ctx));
  }, [
    rawCases,
    programNameById,
    slaMap,
    workflowsById,
    payorNameBySlug,
    currentUserId,
    selectedOrganizationId,
    dispositionLabelsByWorkflowType,
  ]);

  // Client-side program scope. Empty selection = all programs (same array ref).
  const cases: CaseItem[] = useMemo(
    () => filterCasesByPrograms(allCases, selectedProgramIds),
    [allCases, selectedProgramIds],
  );

  const kpi = useMemo(() => {
    const open = cases.length;
    let atRisk = 0;
    let breached = 0;
    for (const c of cases) {
      if (c.riskLevel === 'warning') atRisk += 1;
      else if (c.riskLevel === 'breached') breached += 1;
    }
    return { open, atRisk, breached };
  }, [cases]);

  const filteredCases = useMemo(
    () => (riskFilter === 'all' ? cases : cases.filter(c => c.riskLevel === riskFilter)),
    [cases, riskFilter],
  );

  // O(1) reassign-target lookup. Rebuilt only when the underlying case list
  // changes (typically a 2-min refetch cycle, not per render).
  const rawCasesById = useMemo(() => {
    if (rawCases.length === 0) return EMPTY_RAW_BY_ID;
    const map = new Map<string, GetCaseRecordResponse>();
    for (const c of rawCases) {
      if (c.id) map.set(c.id, c);
    }
    return map;
  }, [rawCases]);

  return {
    cases,
    filteredCases,
    kpi,
    rawCasesById,
    isInitialLoading: tuples.length > 0 && casesLoading,
    isRefreshing: casesFetching && !casesLoading,
    isError: isCasesError,
    /** The selected teams span more programs than the console fans out to; no
     *  cases were fetched. The grid asks the user to select fewer teams. */
    isOverFanout,
  };
}
